import React from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Badge,
  IconButton,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import helpers from 'src/helpers';
import { STORAGE, MESSAGES } from 'src/constants';

const { VCARD_FILENAME } = MESSAGES;

function AttachedFilePrewiew({
  fileName,
  fileUpload,
  isLoading,
  setFileUploadToMessage,
  validateFileInput,
}) {
  const getFileToSrc = (file) => (file ? URL.createObjectURL(file) : null);
  const getFileExtension = () => {
    const name = fileUpload?.file?.name || fileName || '';
    return name.split('.').pop()?.toUpperCase() || '';
  };
  const isAudioFile = () => {
    // CON-1674 — MIME first, because a freshly recorded memo is still a File
    // and carries 'audio/webm;codecs=opus'. Checking video/ first keeps a real
    // video attachment out of the audio branch.
    const mime = (fileUpload?.file?.type || '').toLowerCase();
    if (mime.startsWith('audio/')) return true;
    if (mime.startsWith('video/')) return false;
    // Once uploaded there is no File left, only a URL, so the extension is all
    // there is. 'webm' is included for recorded memos; a video/webm would fall
    // here too, but it would still play its audio rather than break.
    const extension = getFileExtension().toLowerCase();
    return ['mp3', 'wav', 'ogg', 'flac', 'aac', 'amr', 'wma', 'm4a', 'webm'].includes(extension);
  };
  const isContactCard = () => {
    // Check if fileName contains the vCard filename (it may have a URL prefix)
    const name = fileUpload?.file?.name || fileName || '';
    return name.includes(VCARD_FILENAME);
  };

  const handleRemoveSelectedFile = () => {
    setFileUploadToMessage(null);
    validateFileInput(false);
  };
  const audioSrc = fileUpload ? getFileToSrc(fileUpload?.file) : helpers.azure.addSasToUrl(fileName, STORAGE.sas);

  if (isContactCard()) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            paddingRight: '28px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
          }}
        >
          <ContactPageIcon
            sx={{
              color: '#4caf50',
              fontSize: 32,
              marginRight: '8px',
            }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, lineHeight: 1.2 }}
            >
              Your Contact Card
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
            >
              {VCARD_FILENAME}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={handleRemoveSelectedFile}
          disabled={isLoading}
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 18,
            height: 18,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid #e0e0e0',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Box>
    );
  }

  if (isAudioFile()) {
    return (
      <Badge
        overlap="circular"
        size="small"
        badgeContent={(
          <IconButton
            size="small"
            onClick={handleRemoveSelectedFile}
            disabled={isLoading}
            sx={{
              width: 18,
              height: 18,
              right: -20,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
            }}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        )}
      >
        <Box
          sx={{
            width: 200,
            padding: '8px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            position: 'relative'
          }}
        >
          <audio
            controls
            style={{
              width: '100%',
              height: '32px'
            }}
          >
            <source src={audioSrc} />
            <track kind="captions" />
            Your browser does not support the audio tag.
          </audio>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              fontSize: '8px',
              borderRadius: '2px',
              padding: '1px 4px',
            }}
          >
            {getFileExtension()}
          </Typography>
        </Box>
      </Badge>
    );
  }
  return (
    <Badge
      overlap="circular"
      size="small"
      badgeContent={(
        <IconButton
          size="small"
          onClick={handleRemoveSelectedFile}
          disabled={isLoading}
          sx={{
            width: 18,
            height: 18,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      )}
    >
      <Box sx={{ position: 'relative' }}>
        <Avatar
          alt="Attachment Preview"
          src={fileUpload ? getFileToSrc(fileUpload?.file) : helpers.azure.addSasToUrl(fileName, STORAGE.sas)}
          variant="rounded"
          sx={{
            width: 78,
            height: 78,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 2,
            right: 0,
            backgroundColor: 'rgba(134, 127, 127, 0.7)',
            color: 'white',
            textAlign: 'center',
            fontSize: '8px',
            borderRadius: '2px',
            padding: '1px 2px',
          }}
        >
          {getFileExtension()}
        </Typography>
      </Box>
    </Badge>
  );
}

AttachedFilePrewiew.propTypes = {
  setFileUploadToMessage: PropTypes.func.isRequired,
  validateFileInput: PropTypes.func.isRequired,
  fileName: PropTypes.string,
  fileUpload: PropTypes.any,
  isLoading: PropTypes.bool,
};

AttachedFilePrewiew.defaultProps = {
  fileName: null,
  fileUpload: false,
  isLoading: false
};

export default AttachedFilePrewiew;
